export interface SelectOption<T = any> {
  label: string;
  value: T;
  code?: string;
  description?: string;
  symbols?: any[];
}

import assetAttributesData from './asset_attributes.json';

export const RAIL_FORM_OPTIONS = {
  trackTypes: [] as SelectOption[],
  trackDirections: [] as SelectOption[],
  trackSectionTypes: [] as SelectOption[],
  structureTypes: [] as SelectOption[],
  turnoutTypes: [] as SelectOption[],
  trapTypes: [] as SelectOption[],
  deadEndTypes: [] as SelectOption[],
  lcClasses: [] as SelectOption[],
  lcManningTypesOptions: [] as SelectOption[],
  gradientTypes: [] as SelectOption[],
  signalAspects: [] as SelectOption[],
  signalTypes: [] as SelectOption[],
  signalVisibleDirections: [] as SelectOption[],
  signalPlacements: [] as SelectOption[],
  signalIndicatorDirections: [] as SelectOption[],
  riDirectionUpward: [] as SelectOption[],
  riDirectionDownwards: [] as SelectOption[],
  riDirectionVerticalDown: [] as SelectOption[],
  signalSymbols: [] as SelectOption[],
  trackCircuitTypes: [] as SelectOption[],
  distancePairAssetTypes: [] as SelectOption[],

  calTypeOptions: [
    { label: 'SRJ-SRJ', value: 'PT-PT' },
    { label: 'SRJ-Trap point', value: 'PT-DS' },
    { label: 'Trap point-Trap point', value: 'DS-DS' },
    { label: 'FM-FM', value: 'FM-FM' },
    { label: 'SRJ-FM', value: 'PT-FM' },
    { label: 'FM-SRJ', value: 'FM-PT' },
    { label: 'Trap point-SRJ', value: 'DS-PT' },
    { label: 'FM-Trap point', value: 'FM-DS' }
  ] as SelectOption[],

  convertedFromOptions: [
    { label: 'Main to Loop', value: 'main_to_loop' },
    { label: 'Loop to Main', value: 'loop_to_main' }
  ] as SelectOption[],
  convertedToOptions: [
    { label: 'Main to Loop', value: 'main_to_loop' },
    { label: 'Loop to Main', value: 'loop_to_main' }
  ] as SelectOption[],
  trapPointDirections: [
    { label: 'Up-left', value: 'up_left' },
    { label: 'Up-right', value: 'up_right' },
    { label: 'Bottom-left', value: 'down_left' },
    { label: 'Bottom-right', value: 'down_right' }
  ] as SelectOption[],
  workTypeOptions: [
    { label: 'EXISTING', value: 'EXISTING' },
    { label: 'DISMANTLE', value: 'DISMANTLE' },
    { label: 'PROPOSED', value: 'PROPOSED' }
  ] as SelectOption[],

  stationAssetOptions: [
    { label: 'True', value: true },
    { label: 'False', value: false }
  ] as SelectOption<boolean>[],

  directlyConnectedOptions: [
    { label: 'True', value: true },
    { label: 'False', value: false }
  ] as SelectOption<boolean>[],

  standardColors: [
    { label: 'Black', value: '#000000' },
    { label: 'Red', value: '#FF0000' },
    { label: 'Yellow', value: '#FFFF00' },
    { label: 'Green', value: '#00FF00' },
    { label: 'Orange', value: '#FFA500' },
    { label: 'Magenta', value: '#FF00FF' },
    { label: 'Cyan', value: '#00FFFF' },
    { label: 'White', value: '#FFFFFF' }
  ] as SelectOption[],

  lineStyleOptions: [
    { label: 'Solid', value: 'Solid' },
    { label: 'Dashed', value: 'Dashed' },
    { label: 'Dot', value: 'Dot' },
    { label: 'Dash & Dot', value: 'Dash & Dot' }
  ] as SelectOption[],
};

export function buildSignalTypeSymbolsMap(signalTypes: any[]): Record<string, SelectOption[]> {
  const map: Record<string, SelectOption[]> = {};
  if (!Array.isArray(signalTypes)) return map;
  for (const st of signalTypes) {
    const rawSymbols = Array.isArray(st.symbols) ? st.symbols : [];
    const symbolOptions: SelectOption[] = rawSymbols.map((sym: any) => {
      if (typeof sym === 'string') {
        return { label: sym, value: sym };
      }
      const label = sym.name || sym.label || sym.representation?.label || sym.value || '';
      const value = sym.value !== undefined ? sym.value : (sym.name !== undefined ? sym.name : label);
      return {
        label: sym.name || label,
        value: value,
        description: sym.description
      };
    });

    if (st.value) {
      map[String(st.value).toLowerCase()] = symbolOptions;
    }
    if (st.label) {
      map[String(st.label).toLowerCase()] = symbolOptions;
    }
    if (st.name) {
      map[String(st.name).toLowerCase()] = symbolOptions;
    }
    if (st.id) {
      map[String(st.id).toLowerCase()] = symbolOptions;
    }
  }
  return map;
}

export const SIGNAL_TYPE_SYMBOLS_MAP: Record<string, SelectOption[]> = buildSignalTypeSymbolsMap(
  (assetAttributesData as any)?.signals?.signal_types || []
);

export function getAllSignalSymbols(): SelectOption[] {
  const uniqueMap = new Map<string, SelectOption>();
  Object.values(SIGNAL_TYPE_SYMBOLS_MAP).forEach(list => {
    list.forEach(opt => {
      if (opt && opt.value && !uniqueMap.has(String(opt.value).toLowerCase())) {
        uniqueMap.set(String(opt.value).toLowerCase(), opt);
      }
    });
  });
  return Array.from(uniqueMap.values());
}

export const ALL_SIGNAL_SYMBOLS: SelectOption[] = getAllSignalSymbols();

// Prepopulate default signalSymbols from asset_attributes.json
if (RAIL_FORM_OPTIONS.signalSymbols.length === 0) {
  RAIL_FORM_OPTIONS.signalSymbols.push(...ALL_SIGNAL_SYMBOLS);
}

// Prepopulate default distancePairAssetTypes from asset_attributes.json
const defaultDistancePairAssetTypes: SelectOption[] = ((assetAttributesData as any)?.distance_pairs?.asset_types || []).map((opt: any) => ({
  label: opt.label || opt.name || opt.value || opt.code || '',
  value: opt.value,
  code: opt.code,
  description: opt.description
}));
if (RAIL_FORM_OPTIONS.distancePairAssetTypes.length === 0) {
  RAIL_FORM_OPTIONS.distancePairAssetTypes.push(...defaultDistancePairAssetTypes);
}

export function getSymbolsForSignalType(typeVal: any): SelectOption[] {
  if (!typeVal) return [];
  const clean = String(typeof typeVal === 'object' ? (typeVal.value || typeVal.label || '') : typeVal).trim().toLowerCase();
  if (!clean) return [];
  return SIGNAL_TYPE_SYMBOLS_MAP[clean] || [];
}

export function applyApiAttributeTypes(apiData: any): void {
  if (!apiData || typeof apiData !== 'object') return;

  const mapOptions = (list: any[]): SelectOption[] => {
    if (!Array.isArray(list)) return [];
    return list.map((opt: any) => {
      if (typeof opt === 'string') {
        return { label: opt, value: opt };
      }
      const label = opt.label || opt.name || opt.value || opt.code || '';
      const value = opt.value !== undefined ? opt.value : (opt.code !== undefined ? opt.code : (opt.id !== undefined ? opt.id : (opt.name !== undefined ? opt.name : label)));
      return {
        label,
        value,
        code: opt.code,
        description: opt.description,
        symbols: opt.symbols
      };
    });
  };

  const setOptions = (targetKey: keyof typeof RAIL_FORM_OPTIONS, list: any[] | undefined) => {
    if (!list || !Array.isArray(list) || list.length === 0) return;
    const mapped = mapOptions(list);
    if (mapped.length === 0) return;
    const target = RAIL_FORM_OPTIONS[targetKey];
    if (Array.isArray(target)) {
      target.length = 0;
      target.push(...mapped);
    }
  };

  if (apiData.tracks) {
    setOptions('trackTypes', apiData.tracks.track_types);
    setOptions('trackDirections', apiData.tracks.track_directions);
    setOptions('trackSectionTypes', apiData.tracks.track_section_types);
  }

  if (apiData.structures) {
    setOptions('structureTypes', apiData.structures.structure_types);
  }

  if (apiData.turnouts) {
    setOptions('turnoutTypes', apiData.turnouts.turnout_types);
  }

  if (apiData.trap_points) {
    setOptions('trapTypes', apiData.trap_points.trap_point_types);
    setOptions('trapPointDirections', apiData.trap_points.trap_point_directions);
  }

  if (apiData.gates) {
    setOptions('lcClasses', apiData.gates.lc_classes);
    setOptions('lcManningTypesOptions', apiData.gates.lc_manning_types);
  }

  if (apiData.dead_ends) {
    setOptions('deadEndTypes', apiData.dead_ends.dead_end_types);
  }

  if (apiData.gradients) {
    setOptions('gradientTypes', apiData.gradients.gradient_types);
  }

  if (apiData.signals) {
    setOptions('signalAspects', apiData.signals.signal_aspects);
    if (apiData.signals.signal_types?.length) {
      setOptions('signalTypes', apiData.signals.signal_types);
      const apiMap = buildSignalTypeSymbolsMap(apiData.signals.signal_types);
      Object.assign(SIGNAL_TYPE_SYMBOLS_MAP, apiMap);
    }
    setOptions('signalVisibleDirections', apiData.signals.visible_directions);
    setOptions('signalPlacements', apiData.signals.placements);
    if (apiData.signals.indicator_directions?.length) {
      setOptions('signalIndicatorDirections', apiData.signals.indicator_directions);
      setOptions('riDirectionUpward', apiData.signals.indicator_directions);
      setOptions('riDirectionDownwards', apiData.signals.indicator_directions);
      setOptions('riDirectionVerticalDown', apiData.signals.indicator_directions);
    }
    if (apiData.signals.symbols?.length) {
      setOptions('signalSymbols', apiData.signals.symbols);
    } else if (apiData.signals.signal_symbols?.length) {
      setOptions('signalSymbols', apiData.signals.signal_symbols);
    } else if (apiData.signals.indicator_symbols?.length) {
      setOptions('signalSymbols', apiData.signals.indicator_symbols);
    } else if (RAIL_FORM_OPTIONS.signalSymbols.length === 0) {
      RAIL_FORM_OPTIONS.signalSymbols.push(...getAllSignalSymbols());
    }
  }

  if (apiData.track_circuits) {
    setOptions('trackCircuitTypes', apiData.track_circuits.track_circuit_types);
  }

  if (apiData.distance_pairs) {
    setOptions('distancePairAssetTypes', apiData.distance_pairs.asset_types);
  }
}

/**
 * Resolves an asset type identifier (internal type code or UUID)
 * to the exact Distance Pair Asset Type UUID expected by the backend API.
 */
export function resolveDistancePairAssetTypeUuid(typeIdentifier: any): string | undefined {
  if (!typeIdentifier) return undefined;
  const raw = String(typeof typeIdentifier === 'object' ? (typeIdentifier.value || typeIdentifier.code || '') : typeIdentifier).trim();
  const upper = raw.toUpperCase();
  const matched = RAIL_FORM_OPTIONS.distancePairAssetTypes.find(
    opt => opt.code === upper || opt.value === raw
  );
  return matched?.value || raw;
}

/**
 * Resolves a Distance Pair Asset Type UUID or code to its internal UI type representation.
 */
export function resolveDistancePairAssetTypeInfo(uuidOrCode: any): { type: string; typeLabel: string; uuid: string } | undefined {
  if (!uuidOrCode) return undefined;
  const raw = String(typeof uuidOrCode === 'object' ? (uuidOrCode.value || uuidOrCode.code || '') : uuidOrCode).trim();
  const upper = raw.toUpperCase();
  const matched = RAIL_FORM_OPTIONS.distancePairAssetTypes.find(
    opt => opt.value === raw || opt.code === upper
  );
  if (!matched) return undefined;
  return {
    type: (matched.code || '').toLowerCase(),
    typeLabel: matched.label,
    uuid: matched.value
  };
}

/**
 * Normalizes any variation of CAL Type string into the backend canonical codes:
 * PT-PT, PT-DS, DS-DS, FM-FM, PT-FM, FM-PT, DS-PT, FM-DS
 * where PT = stock_rail_joint, DS = trap_point, FM = fouling_mark.
 */
export function normalizeCalType(val: string | null | undefined): string | null {
  if (!val) return null;
  const str = String(val).trim().toUpperCase();
  if (!str) return null;

  const validCodes = ['PT-PT', 'PT-DS', 'DS-DS', 'FM-FM', 'PT-FM', 'FM-PT', 'DS-PT', 'FM-DS'];
  if (validCodes.includes(str)) return str;

  // Handle forms like SRJ-Trap point, TRAP_POINT-SRJ, etc.
  const parts = str.split(/[\s_\-\/]+/).filter(Boolean);
  if (parts.length >= 2) {
    const parseCode = (p: string): string | null => {
      if (p === 'PT' || p === 'PS' || p === 'SRJ' || p.includes('STOCK')) return 'PT';
      if (p === 'DS' || p.includes('TRAP')) return 'DS';
      if (p === 'FM' || p.includes('FOUL')) return 'FM';
      return null;
    };
    const left = parseCode(parts[0]);
    const rest = parts.slice(1).join(' ');
    const right = parseCode(rest) || parseCode(parts[parts.length - 1]);
    if (left && right) {
      const candidate = `${left}-${right}`;
      if (validCodes.includes(candidate)) {
        return candidate;
      }
    }
  }

  if (str.includes('SRJ') && str.includes('TRAP')) {
    return str.startsWith('TRAP') ? 'DS-PT' : 'PT-DS';
  }
  if (str.includes('SRJ') && str.includes('FM')) {
    return str.startsWith('FM') ? 'FM-PT' : 'PT-FM';
  }
  if (str.includes('TRAP') && str.includes('FM')) {
    return str.startsWith('TRAP') ? 'DS-FM' : 'FM-DS';
  }
  if (str.includes('SRJ')) return 'PT-PT';
  if (str.includes('TRAP')) return 'DS-DS';
  if (str.includes('FM')) return 'FM-FM';

  return null;
}

/**
 * Maps the side of a CAL Type code (PT, DS, FM) to its backend asset_type_name.
 * PT -> stock_rail_joint
 * DS -> trap_point
 * FM -> fouling_mark
 */
export function getCalElementAssetTypeName(
  calType: string | null | undefined,
  position: 'left' | 'right'
): 'stock_rail_joint' | 'trap_point' | 'fouling_mark' | null {
  const normType = normalizeCalType(calType);
  if (!normType) return null;
  const parts = normType.split('-');
  const code = position === 'left' ? parts[0] : parts[1];
  switch (code) {
    case 'PT':
      return 'stock_rail_joint';
    case 'DS':
      return 'trap_point';
    case 'FM':
      return 'fouling_mark';
    default:
      return null;
  }
}

/**
 * Returns user-facing label for a CAL Type code (e.g. PT-DS -> 'SRJ-Trap point').
 */
export function getCalTypeDisplayLabel(calType: string | null | undefined): string {
  const norm = normalizeCalType(calType);
  if (!norm) return '';
  const opt = RAIL_FORM_OPTIONS.calTypeOptions.find(o => o.value === norm);
  return opt ? opt.label : norm;
}
